/* eslint-disable @typescript-eslint/no-unused-vars */
import {useEffect, useState} from 'react';

import {TelehealthBookingEnum, TherapyFlagEnum} from 'presentation/constants';
import {formatAddress} from 'presentation/utils';
import {BookingTypeEnum, EntryPointCodeEnum, FlowEnum} from '../../constants';

import {useRebooking} from '../useRebooking/useRebooking';

import {differenceInDays, isValid, parse} from 'date-fns';
import {TypeLocalNameEnum} from 'domain/usecases';
import {useGetOdontoProviderAddress} from 'presentation/modules/booking/odonto/ui/pages/OdontoBooking/steps/OdontoProfessionalSelection/hooks/useGetOdontoProviderAddress';
import {CheckDentalAddressParams} from 'presentation/modules/booking/odonto/ui/pages/OdontoBooking/steps/OdontoProfessionalSelection/hooks/useGetOdontoProviderAddress/types';
import {ErrorsAuthorization} from 'presentation/modules/booking/shared/constants/errorsAuthorizationEnum';
import {TELEMEDICINE_INFO} from 'presentation/modules/booking/shared/constants/telemedicineInfo';
import {useBookingAuthorization} from 'presentation/modules/booking/shared/hooks/useBookingAuthorization';
import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useDisabledSelectionDateStore} from 'presentation/modules/booking/shared/store/useDisabledSelectionDateStore';
import {getTelehealthStepPosition} from 'presentation/modules/booking/telehealth/utils/getTelehealthStepPosition';
import {useQueryParams} from 'presentation/pages/private/hooks';
import {useNavigate} from 'react-router-dom';
import {useGetTelehealthMostRecentDate} from '../../queries/useGetTelehealthMostRecentDate';
import {
	AuthModal,
	HandleHourSelectionParams,
	OdontoAddressModal,
	UseHourSelectionProps,
} from './types';
import {CONSULTATION_CLINIC_ID} from 'presentation/modules/booking/shared/constants/odontoConsultationID';

export function useBookingHourSelection({flowType, handleNext}: UseHourSelectionProps) {
	const setActiveBookingFlowStep = useBookingFlowStepsStore(state => state.setBookingStep);
	const navigate = useNavigate();
	const {clearSearch} = useQueryParams();
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const {rebookingState} = useRebooking({flowType});
	const setDisabledSelectionDate = useDisabledSelectionDateStore(
		state => state.setDisabledSelectionDate,
	);

	const {checkDentalAddress} = useGetOdontoProviderAddress();
	const {verifyHealthDateAuthorization} = useBookingAuthorization();

	const [odontoAddressModal, setOdontoAddressModal] = useState<OdontoAddressModal>('none');
	const [authModal, setAuthModal] = useState<AuthModal>('none');
	const [openSearchBestOptionModal, setOpenSearchBestOptionModal] = useState(false);

	const flTipo = rebookingState ? TherapyFlagEnum.REBOOKING : TherapyFlagEnum.BOOKING;
	const isOdonto = flowType === FlowEnum.ODONTO;
	const isTelehealth = flowType === FlowEnum.TELEHEALTH;
	const isHealth = flowType === FlowEnum.HEALTH;

	useEffect(() => {
		setDisabledSelectionDate(authModal !== 'none' || odontoAddressModal !== 'none');
	}, [authModal, odontoAddressModal, setDisabledSelectionDate]);

	async function checkAuthorization(date: string) {
		if (bookingData.multipleBookings && !bookingData.isRebooking) {
			try {
				setAuthModal('loading');
				const response = await verifyHealthDateAuthorization({
					flTipo,
					healthSelectedDate: date,
					healthSelectedSpecialty: bookingData.specialtyCode,
				});

				if (response.cdRetorno === ErrorsAuthorization.NEED_AUTHORIZATION) {
					setAuthModal('needAth');
					return false;
				}

				if (response.cdRetorno === ErrorsAuthorization.UNAVAILABLE_SLOTS) {
					setAuthModal('allScheduled');
					return false;
				}

				if (response.cdRetorno === ErrorsAuthorization.MINIMUM_INTERVAL) {
					setAuthModal('minimumInterval');
					return false;
				}
				setAuthModal('none');
				return true;
			} catch (error) {
				return setAuthModal('error');
			}
		}
		return true;
	}

	async function checkOdontoAddressIsValid(params: CheckDentalAddressParams) {
		try {
			setOdontoAddressModal('loading');
			const response = await checkDentalAddress({
				dateAt: params.dateAt,
				professionalCode: params.professionalCode,
				specialtyCode: params.specialtyCode,
				unitCode: params.unitCode,
			});
			setOdontoAddressModal('none');

			return response;
		} catch (error) {
			return setOdontoAddressModal('error');
		}
	}

	const handleOpenSearchBestOptionModal = () => {
		setOpenSearchBestOptionModal(true);
	};

	const handleCloseSearchBestOptionModal = () => {
		setOpenSearchBestOptionModal(false);
	};

	const {
		mutateAsync: mutateTelehealthMostRecentDate,
		isPending: isLoadingTelehealthMostRecentDate,
	} = useGetTelehealthMostRecentDate({
		specialty_code: bookingData.specialtyCode,
		entryPointCode: Number(EntryPointCodeEnum.V2),
		subspecialty_code: bookingData.subSpecialtyCode ?? '',
	});

	async function handleHourSelection({
		hour,
		profCode,
		profName,
		date,
		recommendations,
	}: HandleHourSelectionParams) {
		setBookingData(prev => ({
			...prev,
			professionalCode: profCode,
			professionalName: profName,
			dateAt: date,
			hourAt: hour,
		}));

		if (flowType === FlowEnum.ODONTO && recommendations && recommendations.length > 0) {
			setBookingData(prev => ({
				...prev,
				odontoRecommendations: recommendations[0],
			}));
		}

		if (isOdonto) {
			const checkDental = await checkOdontoAddressIsValid({
				dateAt: date,
				professionalCode: profCode,
				specialtyCode: bookingData.specialtyCode,
				unitCode: bookingData.unitCode,
			});
			const isDentalClinic = bookingData.unitCode === CONSULTATION_CLINIC_ID;

			if (checkDental && isDentalClinic) {
				setBookingData(prev => ({
					...prev,
					unitAddress: formatAddress({
						type: checkDental?.cdTipoLogradouro,
						address: checkDental?.nmRuaEndereco,
						number: checkDental?.nuEndereco.toString(),
						complement: checkDental?.dsComplEndereco,
						district: checkDental?.nmBairroEndereco,
						city: checkDental?.nmCidadeEndereco,
						state: checkDental?.cdUfEndereco,
						zipCode: checkDental?.cdCepEmdereco.toString(),
						reference: checkDental?.dsPontoReferencia,
					}),
				}));
			}
		}

		if (isTelehealth) {
			const canGoToNextStep = await checkAuthorization(date);
			if (canGoToNextStep) {
				handleNext();
			}
			return;
		}

		if (isHealth && bookingData?.hasTelehealth) {
			try {
				const telehealthMostRecentDate = await mutateTelehealthMostRecentDate();

				if (!telehealthMostRecentDate) {
					handleNext();
					return;
				}

				const parsedDate = parse(date, 'dd/MM/yyyy', new Date());
				const parsedMostRecentTelehealthDate = parse(
					telehealthMostRecentDate,
					'dd/MM/yyyy',
					new Date(),
				);

				if (!isValid(parsedDate) || !isValid(parsedMostRecentTelehealthDate)) {
					handleNext();
					return;
				}

				const difference = differenceInDays(parsedDate, parsedMostRecentTelehealthDate);
				const minimalDifferenceBetweenHealthAndTelehealth = 5;

				if (difference >= minimalDifferenceBetweenHealthAndTelehealth) {
					handleOpenSearchBestOptionModal();
					return;
				}
			} catch (error) {
				handleNext();
				return;
			}
		}

		handleNext();
	}

	function goToTelehealthProfessionalSelection() {
		handleCloseSearchBestOptionModal();

		setBookingData(prev => ({
			...prev,
			unitCode: TELEMEDICINE_INFO.code,
			unitName: TELEMEDICINE_INFO.name,
			unitAddress: '',
			ufCode: TELEMEDICINE_INFO.ufCode,
			ufName: TELEMEDICINE_INFO.ufName,
			cityName: TELEMEDICINE_INFO.cityName,
			bookingRedirect: true,
			bookingType: BookingTypeEnum.TELEHEALTH,
			hourAt: '',
			dateAt: '',
			professionalCode: '',
			professionalName: '',
			typeLocalName: TypeLocalNameEnum.CIDADE,
		}));

		const {PROFESSIONAL_STEP} = getTelehealthStepPosition();

		setActiveBookingFlowStep(PROFESSIONAL_STEP);
		navigate(TelehealthBookingEnum.TELEHEALTH_BOOKING_FLOW);
		clearSearch();
	}

	function goToNextStep() {
		handleCloseSearchBestOptionModal();
		handleNext();
	}

	return {
		odontoAddressModal,
		setOdontoAddressModal,
		authModal,
		setAuthModal,
		handleHourSelection,
		openSearchBestOptionModal,
		goToTelehealthProfessionalSelection,
		goToNextStep,
		isLoadingTelehealthMostRecentDate,
	};
}
